import { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { querykey } from '@/consts';
import { useQueryDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { WorkspaceFileSummaryContext } from './Context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type Props = {
  fileId: number;
} & ChildrenProps;

export function SummaryContainer(props: Props) {

  const [isRegenerating, setIsRegenerating] = useState(false);

  const apiClient = useChatApiClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: querykey.Workspaces.FileSummary.Get({ fileId: props.fileId }),
    queryFn: () => {
      return apiClient.workspaces.getFileSummary({
        fileId: props.fileId,
      });
    },
  });

  useEffect(() => {
    if (data) {
      if (data?.summary?.status === 'pending') {
        setIsRegenerating(true);
      } else {
        setIsRegenerating(false);
      }
    }
  }, [data]);

  const regenerateMutation = useMutation({
    mutationKey: querykey.Workspaces.FileSummary.Post({ fileId: props.fileId }),

    mutationFn: () => {
      return apiClient.workspaces.regenerateFileSummary({
        fileId: props.fileId,
      });
    },
    onMutate: () => {
      setIsRegenerating(true);
    },
    onSuccess: () => {
      refetch();
    },
  });

  const handleRegenerate = useCallback(() => {
    return regenerateMutation.mutateAsync();
  }, [regenerateMutation]);

  const downloader = useQueryDownloader({
    queryKey: [
      `brand-insights:chat:download`,
      props.fileId,
    ],
    queryFn: ({ queryKey: [, fileId] }) => {
      return apiClient.workspaces.downloadFileSummary({ fileId });
    },
    meta: {
      downloader: true,
    },
    downloaderOptions: {
      onResponse: res => ({
        filename: res.filename,
        type: 'blob',
        value: res.blob,
      }),
    },
  });

  const handleDownload = useCallback(() => {
    return downloader.download({
      title: `Generating Export`,
      name: `File Summary`,
      extension: 'docx',
    });
  }, [downloader]);

  const value = {
    summary: data?.summary,
    isLoading,
    download: handleDownload,
    refetch,
    regenerate: handleRegenerate,
    isRegenerating,
  };

  return (
    <WorkspaceFileSummaryContext.Provider value={value}>
      {props.children}
    </WorkspaceFileSummaryContext.Provider>
  );
}

export default SummaryContainer;